<template>
  <define-search-bar>
    <div>
      <base-button
        v-style:w="{ lg: '15.59375rem' }"
        class="h-9 w-full flex items-center b rounded-full bg-white/20 pr-10 backdrop-blur duration"
        :class="isDark && !isScrolledDown || !isDark && isScrolledDown || !transparent ? 'b-brand-4 c-brand-4' : 'b-white c-white'"
        style="padding-left: 0.625rem"
        @click="openSearchPanel"
      >
        <vf-icon name="search" size="md" />
        <span class="body-3 ws-nowrap" style="margin-left: 0.875rem">
          {{ $t.searchPlaceholder }}
        </span>
      </base-button>
    </div>
  </define-search-bar>

  <!-- Header Top - Scroll Away -->
  <div
    v-show="!hidden"
    ref="promoBarRef"
    v-style="{ 'grid-cols': { sm: '1fr', lg: 'auto 1fr auto' }, 'h': { sm: '2rem', lg: '1.625rem' } }"
    class="relative z-header grid items-center gap-6 px-4 py-2 c-brand-5 lg:px-6 lg:py-1"
    :class="isDark ? 'bg-brand-4' : 'bg-brand-6'"
  >
    <vf-location-selector class="body-5 <lg:hidden" :style="`min-width: ${pxToRem(accountButtonRef?.$el.clientWidth)}`" />
    <div class="relative h-full">
      <!-- Promo Bar -->
      <client-only>
        <transition name="fade">
          <lazy-cms-section
            v-if="showPromoBar && !disablePromoBarOnRoutes.includes($route.path) && megaMenu?.promoBar"
            data-scroll-el="promo-bar"
            :section="sections.promoBar"
          />
        </transition>
      </client-only>
    </div>

    <base-button ref="accountButtonRef" class="body-5 flex items-center gap-1 <lg:hidden" @click="openAccountPanel">
      <span class="sr-only">{{ $t.myAccount }}</span>
      <vf-icon class="pointer-events-none" name="account" />
      <template v-if="user.isLoyaltyMember">
        <span class="fw-medium">
          {{ greeting }}
        </span>
        <template v-if="user.isLoyaltyMember">
          <template
            v-if="!loyalty.vouchersPending
              && !loyalty.vouchers?.applicableLoyaltyVouchers?.length && user.loyaltyPointsBalance"
          >
            | {{ user.loyaltyPointsBalance }} {{ $pluralize($t.points, user.loyaltyPointsBalance) }}
          </template>
          <template v-else-if="loyalty.vouchers?.applicableLoyaltyVouchers?.[0]?.available">
            | {{ replaceAll($t.loyaltyRewardsDetails.rewardAmount, { reward: loyalty.currentReward }) }}
          </template>
        </template>
      </template>
      <template v-else>
        {{ greeting }}
      </template>
    </base-button>
  </div>

  <!-- Header Main - Sticky -->
  <div v-show="!hidden" class="sticky top-0 z-above-header" data-scroll-el="header">
    <header
      ref="headerRef"
      class="b-b b-transparent"
      :class="[
        {
          'inset-x-0 bottom-a top-0 absolute': transparent,
          'bg-op-80': isScrolledDown && transparent,
          'b-grey-80': isScrolledDown && !transparent,
          'bg-op-0': !isScrolledDown,
          'duration': !isSomethingElseSticky,
        },
        isDarkInverse ? 'bg-white' : 'bg-black',
      ]"
    >
      <div
        class="w-full flex items-center py-2 pl-4 pr-2 ease-out lg:grid lg:cols-[1fr_auto_1fr] lg:px-3 <lg:between"
        :class="transparent ? 'bg-transparent' : 'bg-white'"
      >
        <!-- Logo - Mobile -->
        <base-link class="lg:hidden" to="/">
          <span class="sr-only">{{ $t.brand }} {{ $t.home }}</span>
          <vf-icon
            :class="{
              'c-brand-4': transparent && isDarkInverse,
              'c-white': transparent && !isDarkInverse,
            }"
            height="25"
            src="/img/logos/timberland/full.svg"
            width="144"
          />
        </base-link>

        <!-- Menu Button - Desktop -->
        <base-button class="group lg:mr-a <lg:hidden" @click="openMegaMenu">
          <span class="items-center gap-1" :class="navItemHoverClasses" style="display: flex">
            <vf-icon name="menu" size="md" />
            {{ $t.menu }}
          </span>
        </base-button>

        <!-- Logo - Desktop -->
        <div class="flex grow center <lg:hidden">
          <base-link class="py-2" to="/">
            <span class="sr-only">{{ $t.brand }} {{ $t.home }}</span>
            <vf-icon
              class="transition-transform duration-500"
              :class="{
                'c-white': transparent && !isDarkInverse,
                'c-brand-4': transparent && isDarkInverse,
              }"
              height="40"
              src="/img/logos/timberland/full.svg"
              :style="{ transform: `scale(${isScrolledDown ? 1 : 1.3})` }"
              width="220"
            />
          </base-link>
        </div>

        <section class="ml-a flex center gap-1 lg:gap-6" data-test-id="menu-section">
          <!-- Clear Session Button -->
          <vf-button
            v-if="showClearSessionButton && buyInStore.storeInfo?.storeId"
            size="tiny"
            variant="primary"
            @click="buyInStore.clearSession"
          >
            {{ $t.clearSession }}
          </vf-button>

          <!-- Search Bar - Desktop -->
          <search-bar class="<lg:hidden" />

          <!-- Search Button - Mobile -->
          <transition name="fade">
            <base-button
              :aria-label="$t.searchIconLabel"
              class="group lg:hidden"
              @click="openSearchPanel"
            >
              <span :class="navItemHoverClasses">
                <vf-icon name="search" size="md" />
              </span>
            </base-button>
          </transition>

          <!-- Cart -->
          <base-link
            class="group relative"
            to="/cart"
            @click="$gtm.push('topNav.onClickUtilityLink', 'Cart')"
          >
            <span :class="navItemHoverClasses">
              <vf-icon name="bag" size="md" />
              <span class="sr-only">
                {{ replaceAll($pluralize($t.cartIconLabel, cart?.totalItems), { count: cart.totalItems }) }}
              </span>
              <client-only>
                <span
                  v-if="cart?.totalItems"
                  aria-hidden="true"
                  class="absolute right-0 top-0 m-2 h-3 w-3 flex select-none center rounded-full bg-brand-3 text-micro c-white ring ring-white lg:m-1 lg:h-4 lg:w-4 lg:text-xs"
                  style="line-height: 1"
                >
                  {{ cart.totalItems }}
                </span>
              </client-only>
            </span>
          </base-link>

          <!-- Menu Button - Mobile -->
          <base-button
            :aria-label="$t.menuIconLabel"
            class="group lg:hidden"
            @click="openMegaMenu"
          >
            <span :class="navItemHoverClasses">
              <vf-icon name="menu" size="md" />
            </span>
          </base-button>
        </section>
      </div>
    </header>
  </div>

  <!-- SEO: Hidden Mega Menu for Search Engines -->
  <base-lazy-hydrate when="never">
    <vf-mega-menu class="!hidden" :items="megaMenu" :links />
  </base-lazy-hydrate>

  <panel-search-hub :size="{ sm: 'full', lg: 'lg' }" />
  <panel-mega-menu class-content="lg:w-a bg-white/90" :position="{ sm: 'bottom', lg: 'left' }" size="full" />
  <panel-account size="full" />
</template>

<script lang="ts" setup>
import { HeaderContextKey } from '#commerce/components/vf/header/context'

const { transparentMode = undefined, colorMode = undefined } = defineProps<{
  transparentMode?: boolean
  colorMode?: 'light' | 'dark'
  hidden?: boolean
}>()

const { isSomethingElseSticky, links, megaMenu, sections, showPromoBar } = inject(HeaderContextKey)!

const { showClearSessionButton } = useAppConfig().checkout.buyInStore
const auth = useAuthStore()
const buyInStore = useBuyInStoreStore()
const cart = useCartStore()
const { DialogSignIn } = useDialogsStore()
const { disablePromoBarOnRoutes } = useFeatureFlags()
const header = useHeaderStore()
const loyalty = useLoyaltyStore()
const { $gtm, $t } = useNuxtApp()
const { y } = useScroll(document)
const user = useUserStore()

const PanelAccount = createPanel('account')
const PanelMegaMenu = createPanel('mega-menu')
const PanelSearchHub = createPanel('search-hub')

const [DefineSearchBar, SearchBar] = createReusableTemplate()

const accountButtonRef = ref()
const headerRef = toRef(header.refs, 'header')
const promoBarRef = toRef(header.refs, 'promoBar')

const mode = computed(() => colorMode ?? header.mode)
const transparent = computed(() => transparentMode ?? header.transparent)
const isDark = computed(() => mode.value === 'dark')
const isScrolledDown = computed(() => y.value > 25)
const isDarkInverse = computed(() => isDark.value !== isScrolledDown.value)

const greeting = computed(() =>
  user.details?.firstName
    ? replaceAll($t.loyaltyRewardsDetails.greetingWithName, { name: user.details.firstName })
    : $t.loyaltyRewardsDetails.greeting
)

const navItemHoverClasses = computed(() => ([
  'p-2 rounded-full block duration',
  transparent.value ? 'group-@hover:bg-brand-1/10' : 'group-@hover:c-black group-@hover:bg-grey-80',
  {
    'c-white': transparent.value && !isDarkInverse.value,
    'c-brand-4': transparent.value && isDarkInverse.value,
  }
]))

const openAccountPanel = () => {
  if (auth.loggedIn) {
    $gtm.push('topNav.onClickUtilityLink', 'My account')
    PanelAccount.open()
  }
  else {
    $gtm.push('topNav.onClickUtilityLink', 'Sign In')
    DialogSignIn.open({ formLocation: 'modal:single:header:none' })
  }
}

const openMegaMenu = () => {
  $gtm.push('topNav.onClickHamburgerMenu', true)
  PanelMegaMenu.open({ items: megaMenu, links: links.value }).then((backdropClicked) => {
    if (backdropClicked) $gtm.push('topNav.onClickHamburgerMenu', false)
  })
}

const openSearchPanel = async () => {
  $gtm.push('topNav.onClickUtilityLink', 'Search')
  PanelSearchHub.open()
}
</script>
